import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  TextField,
  Button,
  CardMedia,
  IconButton,
  Input,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  useAddBoostPlansMutation,
  useUpdateBoostPlansMutation,
  useLazyGetBoostPlansByIdQuery,
} from "../../service/boostPlans";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditText from "../../components/editText";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showError, showToast } from "../../constants/toasts";
import { isNumber, isValidInput } from "../../utils/validations";
import { useGetCatDropDownListQuery } from "../../service/category";
import usePermission from "../../hooks/usePermission";

const AddBoostPlans = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  usePermission({
    label: "Manage Boost Plans",
    page_permission: id ? "isEdit" : "isAdd",
  });

  const [addBoostPlan] = useAddBoostPlansMutation();
  const [updateBoostPlan] = useUpdateBoostPlansMutation();
  const [getBoostPlanById] = useLazyGetBoostPlansByIdQuery();
  const { data: catData } = useGetCatDropDownListQuery({});
  const categoryData = catData?.data?.map(
    (item: { name: string; _id: string }) => ({
      _id: item?._id,
      name: item?.name,
    })
  );
  const isEdit = Boolean(id);

  const validationSchema = Yup.object({
    title: Yup.string()
      .required("Title is required")
      .max(100, "Max 100 characters"),
    duration: Yup.number()
      .typeError("Duration must be a number")
      .required("Duration is required")
      .integer("Duration must be an integer")
      .min(1, "Minimum 1 day"),
    price: Yup.number()
      .typeError("Price must be a number")
      .required("Price is required")
      .min(0, "Price cannot be negative"),
    category: Yup.string().required("Category is required"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      duration: "",
      price: "",
      category: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const payload: any = {
          title: values.title,
          duration: Number(values.duration),
          price: Number(values.price),
          category: values.category,
        };
        let res;
        if (isEdit) {
          res = await updateBoostPlan({
            id: id as string,
            body: payload,
          }).unwrap();
          if (
            res?.code === 200 ||
            res?.statusCode === 200 ||
            res?.status === 200
          ) {
            showToast(res?.message || "Boost plan updated successfully");
            navigate("/manage-boost-plans");
          } else {
            showError(res?.message || "Failed to update boost plan");
          }
        } else {
          res = await addBoostPlan(payload).unwrap();
          if (
            res?.code === 200 ||
            res?.statusCode === 200 ||
            res?.status === 200
          ) {
            showToast(res?.message || "Boost plan added successfully");
            navigate("/manage-boost-plans");
          } else {
            showError(res?.message || "Failed to add boost plan");
          }
        }
      } catch (e: any) {
        console.error("Error saving boost plan:", e);
        showError(e?.data?.message || "Failed to save boost plan");
      }
    },
  });

  useEffect(() => {
    const load = async () => {
      if (!isEdit) return;
      try {
        const res = await getBoostPlanById(id as any).unwrap();
        const ok =
          res?.code === 200 || res?.statusCode === 200 || res?.status === 200;
        if (ok && res?.data) {
          const data = res.data as any;
          formik.setValues({
            title: data?.title || "",
            duration: data?.duration ?? "",
            price: data?.price ?? "",
            category: data?.category || "",
          });
        } else {
          showError(res?.message || "Failed to fetch boost plan");
          navigate("/manage-boost-plans");
        }
      } catch (e: any) {
        showError(e?.data?.message || "Failed to fetch boost plan");
        navigate("/manage-boost-plans");
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  return (
    <>
      <div className="main_title">
        <h1>{isEdit ? "Edit Boost Plan" : "Add Boost Plan"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-boost-plans")}>
            Manage Boost Plans
          </button>{" "}
          - {isEdit ? "Edit Boost Plan" : "Add Boost Plan"}
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            {/* Title */}
            <div className="control_group w_50">
              <label htmlFor="reward_title">Title</label>
              <TextField
                id="reward_title"
                name="title"
                placeholder="Enter Title"
                fullWidth
                hiddenLabel
                value={formik.values.title}
                onChange={(e) => {
                  const value = e.target.value;
                  if (isValidInput(value) && value.length <= 100) {
                    formik.handleChange(e);
                  }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={
                  formik.touched.title && (formik.errors.title as any)
                }
              />
            </div>

            {/* Duration */}
            <div className="control_group w_33">
              <label htmlFor="duration">Duration</label>
              <TextField
                id="duration"
                name="duration"
                type="text"
                placeholder="Enter Number of Days"
                fullWidth
                hiddenLabel
                inputProps={{ min: 1, max: 365, inputMode: "numeric" }}
                value={formik.values.duration}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only whole numbers, up to 6 digits
                  if (/^\d*$/.test(value) && value.length <= 6) {
                    formik.handleChange(e);
                  }
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.duration && Boolean(formik.errors.duration)
                }
                helperText={
                  formik.touched.duration && (formik.errors.duration as any)
                }
              />
            </div>
            {/* Duration */}
            <div className="control_group w_33">
              <label htmlFor="price">Price</label>
              <TextField
                id="price"
                name="price"
                type="text"
                placeholder="Enter Price"
                fullWidth
                hiddenLabel
                inputProps={{ min: 0, step: "0.01", inputMode: "decimal" }}
                value={formik.values.price}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only numbers with optional single decimal point, up to 2 decimal places
                  if (/^\d*\.?\d{0,2}$/.test(value) && value.length <= 10) {
                    formik.handleChange(e);
                  }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={
                  formik.touched.price && (formik.errors.price as any)
                }
              />
            </div>
            {/* Assign to Category */}
            <div className="control_group w_33">
              <label htmlFor="category">Assign to Category</label>
              <Autocomplete
                disablePortal
                options={categoryData || []}
                getOptionLabel={(o: any) => o.name}
                value={
                  (categoryData || []).find(
                    (c: { _id: string }) => c._id === formik.values.category
                  ) || null
                }
                onChange={(_, val: any) => {
                  formik.setFieldValue("category", val?._id || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Category"
                    hiddenLabel
                    error={
                      formik.touched.category && Boolean(formik.errors.category)
                    }
                    helperText={
                      formik.touched.category && (formik.errors.category as any)
                    }
                  />
                )}
              />
            </div>
          </div>

          <div className="form_btn">
            <Button className="btn btn_primary" type="submit">
              {isEdit ? "Update" : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddBoostPlans;
